import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CheckIcon from '@material-ui/icons/Check';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import SendOutlinedIcon from '@material-ui/icons/SendOutlined';
import { FORM_COLORS } from '../PublicationFormModal/constants';
import { InfoBox } from '../PublicationFormModal/components/InfoBox';
import { ImportPriceRow, ImportPricesModalProps, ImportVariant } from './types';
import { ImportStepsBar } from './ImportStepsBar';
import { isPvpImportRow, parseImportPricesCsv } from './parseImportPricesCsv';

const VARIANT_CONFIG: Record<
  ImportVariant,
  { title: string; columns: string[] }
> = {
  pvp: {
    title: 'Importación masiva — Actualizar PVP y PV',
    columns: ['publication_id', 'precio_pvp', 'precio_pv_campana'],
  },
  'one-payment': {
    title: 'Importación masiva — Actualizar precio 1 pago',
    columns: ['publication_id', 'precio_1_pago'],
  },
};

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 14,
    width: 680,
    maxWidth: '96vw',
  },
  body: {
    padding: '26px 30px 30px',
    fontFamily: 'inherit',
    color: FORM_COLORS.text,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  title: {
    fontWeight: 500,
    fontSize: 16,
  },
  description: {
    fontSize: 13,
    color: FORM_COLORS.textSecondary,
    marginBottom: 14,
  },
  columnsBox: {
    background: FORM_COLORS.bg,
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 18,
  },
  columnsLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: FORM_COLORS.textSecondary,
    margin: '0 0 10px',
  },
  columnsList: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  code: {
    background: FORM_COLORS.white,
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
  },
  actionsRow: {
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
  },
  dropzone: {
    border: `2px dashed ${FORM_COLORS.border}`,
    borderRadius: 10,
    padding: '28px 20px',
    textAlign: 'center',
    background: FORM_COLORS.bg,
    cursor: 'pointer',
    transition: 'all .2s',
  },
  dropzoneActive: {
    borderColor: FORM_COLORS.pink,
    background: FORM_COLORS.pinkLight,
  },
  dropzoneLoaded: {
    borderColor: FORM_COLORS.green,
    background: FORM_COLORS.greenBg,
  },
  dropIcon: {
    fontSize: 30,
    color: FORM_COLORS.textSecondary,
    marginBottom: 8,
  },
  dropText: {
    fontSize: 13,
    color: FORM_COLORS.textSecondary,
    margin: '0 0 8px',
  },
  dropLink: {
    color: FORM_COLORS.pink,
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'underline',
  },
  fileName: {
    fontSize: 13,
    fontWeight: 500,
    color: FORM_COLORS.green,
    margin: '0 0 4px',
  },
  changeFile: {
    background: 'none',
    border: 'none',
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    background: FORM_COLORS.bg,
    padding: '9px 12px',
    textAlign: 'left',
    fontWeight: 500,
    color: FORM_COLORS.textSecondary,
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
  },
  td: {
    padding: '9px 12px',
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
  },
  mono: {
    fontFamily: "'JetBrains Mono', monospace",
  },
  tableWrap: {
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 18,
  },
  badgeOk: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 7px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    background: FORM_COLORS.greenBg,
    color: FORM_COLORS.green,
    border: `0.5px solid ${FORM_COLORS.greenBorder}`,
  },
  primaryButton: {
    background: FORM_COLORS.pink,
    color: '#fff',
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
    transition: 'opacity .15s',
    '&:hover': { opacity: 0.88 },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
  },
  outlineButton: {
    background: 'transparent',
    color: FORM_COLORS.pink,
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
  },
  ghostButton: {
    background: 'none',
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    cursor: 'pointer',
    color: FORM_COLORS.textSecondary,
    fontFamily: 'inherit',
  },
}));

export const ImportPricesModal = ({
  open,
  variant = 'pvp',
  onClose,
  onConfirm,
}: ImportPricesModalProps) => {
  const classes = useStyles();
  const config = VARIANT_CONFIG[variant];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validatedRows, setValidatedRows] = useState<ImportPriceRow[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setFile(null);
      setDragActive(false);
      setValidatedRows([]);
      setValidationError(null);
    }
  }, [open]);

  const downloadTemplate = () => {
    const blob = new Blob([`${config.columns.join(';')}\n`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plantilla_${variant}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (selected?: File | null) => {
    if (selected) {
      setFile(selected);
      setValidationError(null);
      setValidatedRows([]);
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    setValidating(true);
    setValidationError(null);

    const result = await parseImportPricesCsv(file, variant);

    setValidating(false);

    if (!result.success) {
      setValidationError(result.error);
      return;
    }

    setValidatedRows(result.rows);
    setStep(3);
  };

  const handleConfirm = async () => {
    if (validatedRows.length === 0) return;
    try {
      setConfirming(true);
      await onConfirm?.(validatedRows, variant);
      onClose();
    } finally {
      setConfirming(false);
    }
  };

  const dropzoneClass = useMemo(() => {
    if (file) return `${classes.dropzone} ${classes.dropzoneLoaded}`;
    if (dragActive) return `${classes.dropzone} ${classes.dropzoneActive}`;
    return classes.dropzone;
  }, [classes, dragActive, file]);

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false}>
      <div className={classes.body}>
        <div className={classes.header}>
          <span className={classes.title}>{config.title}</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <ImportStepsBar currentStep={step} />

        {step === 1 && (
          <div>
            <p className={classes.description}>
              Descargá la plantilla CSV con el formato correcto y completá los datos.
            </p>
            <div className={classes.columnsBox}>
              <p className={classes.columnsLabel}>Columnas requeridas:</p>
              <div className={classes.columnsList}>
                {config.columns.map((column) => (
                  <code key={column} className={classes.code}>
                    {column}
                  </code>
                ))}
              </div>
            </div>
            <div className={classes.actionsRow}>
              <button type="button" className={classes.outlineButton} onClick={downloadTemplate}>
                <GetAppIcon style={{ fontSize: 16 }} />
                <span>Descargar plantilla CSV</span>
              </button>
              <button type="button" className={classes.ghostButton} onClick={() => setStep(2)}>
                Ya tengo el archivo →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className={classes.description}>Subí el archivo CSV con los datos actualizados.</p>
            <div
              role="button"
              tabIndex={0}
              className={dropzoneClass}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                handleFile(event.dataTransfer.files?.[0]);
              }}
            >
              {file ? (
                <>
                  <DescriptionOutlinedIcon
                    style={{ fontSize: 30, color: FORM_COLORS.green, marginBottom: 8 }}
                  />
                  <p className={classes.fileName}>{file.name}</p>
                  <button
                    type="button"
                    className={classes.changeFile}
                    onClick={(event) => {
                      event.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Cambiar archivo
                  </button>
                </>
              ) : (
                <>
                  <CloudUploadOutlinedIcon className={classes.dropIcon} />
                  <p className={classes.dropText}>Arrastrá el archivo o</p>
                  <span className={classes.dropLink}>seleccioná desde tu equipo</span>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(event) => handleFile(event.target.files?.[0])}
              />
            </div>
            {validationError && (
              <InfoBox tone="amber">{validationError}</InfoBox>
            )}
            <div className={classes.actionsRow} style={{ marginTop: 14 }}>
              <button type="button" className={classes.ghostButton} onClick={() => setStep(1)}>
                ← Atrás
              </button>
              <button
                type="button"
                className={classes.primaryButton}
                disabled={!file || validating}
                onClick={handleValidate}
              >
                <CheckIcon style={{ fontSize: 16 }} />
                <span>{validating ? 'Validando...' : 'Validar archivo →'}</span>
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <InfoBox tone="green">
              {`Archivo validado. Se actualizarán ${validatedRows.length} publicaciones.`}
            </InfoBox>
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>publication_id</th>
                    {variant === 'pvp' ? (
                      <>
                        <th className={classes.th}>precio_pvp</th>
                        <th className={classes.th}>precio_pv</th>
                      </>
                    ) : (
                      <th className={classes.th}>precio_1_pago</th>
                    )}
                    <th className={classes.th}>estado</th>
                  </tr>
                </thead>
                <tbody>
                  {validatedRows.map((row, index) => (
                    <tr key={`${row.publication_id}-${index}`}>
                      <td className={`${classes.td} ${classes.mono}`}>{row.publication_id}</td>
                      {isPvpImportRow(row) ? (
                        <>
                          <td className={classes.td}>{row.precio_pvp}</td>
                          <td className={classes.td}>{row.precio_pv}</td>
                        </>
                      ) : (
                        <td className={classes.td}>{row.precio_1_pago}</td>
                      )}
                      <td className={classes.td}>
                        <span className={classes.badgeOk}>ok</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={classes.actionsRow}>
              <button type="button" className={classes.ghostButton} onClick={() => setStep(2)}>
                ← Atrás
              </button>
              <button
                type="button"
                className={classes.primaryButton}
                disabled={confirming}
                onClick={handleConfirm}
              >
                <SendOutlinedIcon style={{ fontSize: 16 }} />
                <span>Confirmar importación</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
